import { NetworkStatus } from '@apollo/client';
import type {
  DocumentNode,
  ObservableQuery,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { isNetworkRequestInFlight } from '@apollo/client/utilities';
import * as Sentry from '@sentry/core';
import { useEffect, useRef } from 'react';

import { sendSafeError } from '#src/lib/error.ts';
import {
  IncompleteCacheReadError,
  RefetchLoopError,
} from '#src/lib/fingerprintedErrors.ts';

// Apollo writes a network result to the cache and reads it back; only a
// complete read-back is used. An incomplete one is kept as the raw network
// result, which silently skips every `read` policy in the query, so `Date`
// fields arrive as ISO strings and cryptocurrency amounts as strings. Apollo
// names the missing field in a warning that only exists in development builds.
// `cache.diff` runs the same comparison in production. See
// docs/knowledge-vault/concepts/observability/apollo-read-policy-bypass-playbook.md
//
// Only the read-back of the query's own network result is that bug. A later
// write by another query can leave this one incomplete too (normalized entities
// are shared), but Apollo then refetches it on its own: that drift is expected
// unless it never settles, which the refetch loop report covers.
const reportedOperations = new Set<string>();

const REFETCH_LOOP_COUNT = 5;
const REFETCH_LOOP_WINDOW_MS = 10_000;

const operationName = (query: DocumentNode) => {
  for (const definition of query.definitions) {
    if (definition.kind === 'OperationDefinition' && definition.name) {
      return definition.name.value;
    }
  }
  return undefined;
};

type MissingTree = string | { [key: string]: MissingTree };

const missingFieldPaths = (
  tree: MissingTree | undefined,
  prefix: string[] = []
): string[] => {
  if (!tree) return [];
  if (typeof tree === 'string') return [prefix.join('.')];

  return Object.entries(tree).flatMap(([key, subtree]) =>
    missingFieldPaths(subtree, [...prefix, key])
  );
};

type ReportType = 'incomplete-cache-readback' | 'refetch-loop';

const report = (
  reportType: ReportType,
  name: string,
  error: Error,
  extra: Record<string, unknown>
) => {
  reportedOperations.add(`${reportType}:${name}`);

  Sentry.withScope(scope => {
    scope.setTag('kind', name);
    for (const [key, value] of Object.entries(extra)) {
      scope.setExtra(key, value);
    }
    scope.setFingerprint([reportType, name]);
    sendSafeError(error);
  });
};

const alreadyReported = (reportType: ReportType, name: string) =>
  reportedOperations.has(`${reportType}:${name}`);

const withinLoopWindow = (refetchTimes: number[], now: number) => [
  ...refetchTimes.filter(at => now - at < REFETCH_LOOP_WINDOW_MS),
  now,
];

type RenderedState = { networkStatus: NetworkStatus; hasData: boolean };

export const useReportIncompleteCacheRead = ({
  query,
  fetchPolicy,
  observable,
  hasData,
  networkStatus,
  skip,
}: {
  query: DocumentNode;
  fetchPolicy: WatchQueryFetchPolicy | undefined;
  observable: Pick<ObservableQuery, 'getCurrentResult' | 'variables'>;
  hasData: boolean;
  networkStatus: NetworkStatus;
  skip: boolean;
}) => {
  const client = useApolloClient();
  const previousRender = useRef<RenderedState>({ networkStatus, hasData });
  const requestLeftSettledData = useRef(false);
  const refetchTimes = useRef<number[]>([]);

  useEffect(() => {
    if (skip) {
      previousRender.current = { networkStatus, hasData: false };
      refetchTimes.current = [];
      return;
    }

    const previous = previousRender.current;
    previousRender.current = { networkStatus, hasData };

    const requestStarted =
      !isNetworkRequestInFlight(previous.networkStatus) &&
      isNetworkRequestInFlight(networkStatus);
    if (requestStarted) {
      requestLeftSettledData.current =
        previous.networkStatus === NetworkStatus.ready && previous.hasData;
      return;
    }

    const networkResultLanded =
      isNetworkRequestInFlight(previous.networkStatus) &&
      networkStatus === NetworkStatus.ready;
    const resultWrittenToCache = fetchPolicy !== 'no-cache';
    if (!networkResultLanded || !resultWrittenToCache || !hasData) return;

    // `clearStore`/`resetStore` (e.g. on log out) reset every ObservableQuery
    // without emitting, so the component still renders the pre-reset data
    // while the cache is empty. Only the ObservableQuery knows it was dropped.
    const resultDroppedByStoreReset = !observable.getCurrentResult().data;
    if (resultDroppedByStoreReset) return;

    const name = operationName(query);
    if (!name) return;

    const refetchedAfterCacheWrite =
      previous.networkStatus === NetworkStatus.loading &&
      requestLeftSettledData.current;
    if (refetchedAfterCacheWrite) {
      refetchTimes.current = withinLoopWindow(refetchTimes.current, Date.now());
    }
    if (
      refetchTimes.current.length >= REFETCH_LOOP_COUNT &&
      !alreadyReported('refetch-loop', name)
    ) {
      report(
        'refetch-loop',
        name,
        new RefetchLoopError(
          `Refetch loop in ${name}: ${REFETCH_LOOP_COUNT} refetches after cache writes within ${REFETCH_LOOP_WINDOW_MS / 1000}s`
        ),
        {}
      );
    }

    if (alreadyReported('incomplete-cache-readback', name)) return;

    const { complete, missing } = client.cache.diff({
      query,
      variables: observable.variables,
      optimistic: true,
      returnPartialData: true,
    });
    if (complete) return;

    const paths = missingFieldPaths(
      (missing as { path?: MissingTree } | undefined)?.path
    );
    if (!paths.length) return;

    report(
      'incomplete-cache-readback',
      name,
      new IncompleteCacheReadError(
        `Incomplete cache read-back in ${name}: ${paths.join(', ')}`
      ),
      { missingFields: paths }
    );
  }, [client, query, fetchPolicy, observable, hasData, networkStatus, skip]);
};
